import React from "react";

import cx from "classnames";

import { coalesce, setStateValue, setState } from "@kazoohr/helpers";

import type { IconDictionary } from "../Icon/types";
import { IconButton } from "../IconButton";
import { Link } from "../Link";
import { Modal } from "../Modal";
import { ModalActions } from "../ModalActions";
import { ProfilePic } from "../ProfilePic";
import { TextArea } from "../TextArea";

import type {
  OnMentionCallback,
  OnDeleteCallback,
  OnEditCallback,
  UserLike,
} from "./ChatWindow";
import styles from "./ChatWindow.scss";

type ChatMessageLabels = {
  closeAriaLabel?: string;
  deleteIcon?: string;
  deleteHeader?: string;
  deleteConfirmation?: string;
  deleteSaveButton?: string;
  deleteCancelButton?: string;
  editIcon?: string;
  editHeader?: string;
  editSaveButton?: string;
  editCancelButton?: string;
  editErrorMessage?: string;
};

export interface ChatMessageProps {
  currentUserId?: string;
  message: {
    /**
     * Unique ID of the comment
     */
    id: string;
    /**
     * User ID of the commenter
     */
    userId: string;
    /**
     * The displayed message
     */
    message: string | React.ReactNode;
    /**
     * The raw message (DB representation). If not provided, we use the message, unless it's a React component. Then we use a blank string (uneditable).
     */
    rawMessage?: string;
    /**
     * The display time of the string
     */
    displayTime?: string;
  };
  onDelete?: OnDeleteCallback;
  onEdit?: OnEditCallback;
  onMention?: (searchTerm: string, callback: OnMentionCallback) => void;
  user: UserLike;
  labels?: ChatMessageLabels;
  displayMode?: "default" | "compressed";
  profilePicIconOverride?: IconDictionary;
  linkElement?: React.ComponentType<any>;
}

const defaultLabels = {
  closeAriaLabel: "",
  deleteIcon: "",
  deleteHeader: "",
  deleteConfirmation: "",
  deleteSaveButton: "",
  deleteCancelButton: "",
  editIcon: "",
  editHeader: "",
  editSaveButton: "",
  editCancelButton: "",
  editErrorMessage: "",
};

// eslint-disable-next-line max-lines-per-function
export function ChatMessage({
  currentUserId,
  message,
  onDelete,
  onEdit,
  onMention,
  user,
  labels = defaultLabels,
  displayMode,
  profilePicIconOverride,
  linkElement,
}: ChatMessageProps) {
  const myMessage = message.userId === currentUserId;
  const contentClassNames = cx(styles["message-content"], {
    [styles["message-content--ours"]]: myMessage,
    [styles["message-content--theirs"]]: !myMessage,
  });
  const hasMetaActions = myMessage && (onEdit || onDelete);
  const [isDeleteOpen, setDeleteConfirmation] = React.useState(false);
  const [isEditOpen, setEditConfirmation] = React.useState(false);
  const [comment, setComment] = React.useState(
    message.rawMessage || message.message
  );

  const name = user?.name;

  const messageHeaderStyle =
    displayMode === "compressed"
      ? "message-header-compressed"
      : "message-header";

  const messageTextStyle =
    displayMode === "compressed" ? "message-text-compressed" : "message-text";

  return (
    <div
      className={cx(styles["chat-message"])}
      data-testid="chatwindow-message"
      key={message.id}
      id={message.id}
    >
      <div className={styles["content-wrapper"]}>
        <div className={styles["chat-image-container"]}>
          <ProfilePic
            user={user}
            dimensions={32}
            icon={profilePicIconOverride}
          />
        </div>
        <div className={styles["chat-message-container"]}>
          <div className={contentClassNames}>
            <div className={styles[messageHeaderStyle]}>
              <Link
                className={styles["message-name"]}
                to={`/profile/${user.id}`}
                linkElement={linkElement}
              >
                {name}
              </Link>
              <div className={styles["chat-date"]}>{message.displayTime}</div>
            </div>
            <div className={styles[messageTextStyle]}>{message.message}</div>
            {hasMetaActions && (
              <div
                className={styles["meta-actions"]}
                data-testid="chat-window-meta-actions"
              >
                {onEdit && (
                  <>
                    <IconButton
                      aria-label={labels.editIcon}
                      icon="pencil"
                      onClick={setStateValue(setEditConfirmation, !isEditOpen)}
                      className={styles["action-buttons"]}
                      data-testid="chatwindow-message-edit-button"
                      key={`edit-icon-${message.id}`}
                    />
                    <Modal
                      isOpen={isEditOpen}
                      size="small"
                      modalTitle={labels.editHeader}
                      onClose={setStateValue(setEditConfirmation, false)}
                    >
                      <TextArea
                        id="dialog-edit-comment"
                        value={
                          typeof comment === "string"
                            ? comment
                            : // istanbul ignore next - failsafe default. In scenarios where we use custom components, we expect `rawMessage` to be a string. But to make the types easier, we guard it with a simple ternary like this.
                              ""
                        }
                        minRows={3}
                        onChange={setState(setComment)}
                        onMention={onMention}
                      />
                      <ModalActions
                        mainAction={{
                          testId: "dialog-edit-button",
                          size: "small",
                          onClick: getDialogEditCallback({
                            comment,
                            message,
                            setComment,
                            setDeleteConfirmation,
                            setEditConfirmation,
                            onEdit,
                            isDeleteOpen,
                          }),
                          text: coalesce(labels.editSaveButton, ""),
                        }}
                        cancelAction={{
                          testId: "dialog-edit-cancel",
                          size: "small",
                          onClick: setStateValue(setEditConfirmation, false),
                          text: coalesce(labels.editCancelButton, ""),
                        }}
                      />
                    </Modal>
                  </>
                )}
                {onDelete && (
                  <>
                    <IconButton
                      aria-label={labels.deleteIcon}
                      icon="trash"
                      onClick={setStateValue(
                        setDeleteConfirmation,
                        !isDeleteOpen
                      )}
                      className={styles["action-buttons"]}
                      data-testid="chatwindow-message-delete-button"
                      key={`delete-icon-${message.id}`}
                    />
                    <Modal
                      isOpen={isDeleteOpen}
                      size="small"
                      modalTitle={labels.deleteHeader}
                      onClose={setStateValue(setDeleteConfirmation, false)}
                    >
                      {labels.deleteConfirmation}
                      <ModalActions
                        mainAction={{
                          testId: "dialog-delete-button",
                          size: "small",
                          onClick: getDeleteHandler({
                            onDelete,
                            message,
                            setDeleteConfirmation,
                          }),
                          text: coalesce(labels.deleteSaveButton, ""),
                        }}
                        cancelAction={{
                          testId: "dialog-delete-cancel",
                          size: "small",
                          onClick: setStateValue(setDeleteConfirmation, false),
                          text: coalesce(labels.deleteCancelButton, ""),
                        }}
                      />
                    </Modal>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function getDialogEditCallback({
  comment,
  message,
  setComment,
  setDeleteConfirmation,
  setEditConfirmation,
  onEdit,
  isDeleteOpen,
}: {
  setEditConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  setDeleteConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  setComment: React.Dispatch<
    React.SetStateAction<undefined | string | React.ReactNode>
  >;
  onEdit?: OnEditCallback;
  message: { id: string; message?: string | React.ReactNode };
  comment?: string | React.ReactNode;
  isDeleteOpen?: boolean;
}) {
  return () => {
    setEditConfirmation(false);
    if (typeof comment === "string" && comment.trim().length > 0) {
      message.message = comment;
      onEdit?.({ id: message.id, message: comment });
    } else {
      setComment(message.message);
      setDeleteConfirmation(!isDeleteOpen);
    }
  };
}

export function getDeleteHandler({
  onDelete,
  setDeleteConfirmation,
  message,
}: {
  onDelete?: OnDeleteCallback;
  message: { id: string };
  setDeleteConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  return () => {
    setDeleteConfirmation(false);
    onDelete?.(message.id);
  };
}
